/**
 * `runProgramSg` — mirrors WinKFP's `coapiKfProgSgD2`.
 *
 * The flash is **a single IPO dispatch** of `SG_PROGRAMMIEREN`. The
 * IPO encapsulates every wire-level concern (security access via
 * SEED_KEY, programming-session entry, block transfer, post-flash
 * verification). Our job is to seed the right cabd-pars so the IPO
 * knows what to flash and what AIF to stamp post-flash.
 *
 * Verified against `winkfpt.exe` FUN_00455780 (xref to
 * `s_coapiKfProgSgD2_006001c4` at 0x006001c4) — see
 * `docs/architecture.md` §11.8.
 *
 * **Critical dependency:** the IPO reads the `.0PA` / `.0DA` files
 * via CABF* file-IO syscalls. Inpax-interpreter doesn't currently
 * implement those (task #234). Without them, dispatch will succeed
 * but the IPO will fail at the first `CABFOpen` call. Mock-mode
 * dispatch + dry-run still useful for shape validation.
 */
import { buildInstrumentedFirmwareSource, } from './firmware-source.js';
import { newEdiabasJobCounters, wrapWithJobCounter, } from './ediabas-counter.js';
/**
 * Dispatch `SG_PROGRAMMIEREN` against the target SG's IPO. This is
 * the ONLY wire-level call the host makes for a flash — the IPO
 * handles everything from security access to block transfer to
 * post-flash verification internally.
 *
 * `firmwareSource` is what the IPO's flash loop pops chunks from
 * via slot 0x55 — typically built via `buildRegionsFirmwareSource`
 * over the parsed `.0PA`. Without it, the loop exits immediately
 * (no payload), which is the safe degenerate path.
 */
export async function runProgramSg(ecu, ediabas, startRuntime, opts = {}, firmwareSource, onProgress) {
    const cabdPars = {
        DOMINANTE: String(opts.dominante ?? 0),
        BSUTIME: String(opts.bsuTime ?? 0),
        SCHNELLE_BAUDRATE: opts.schnelleBaudrate ? 'ON' : 'OFF',
        PROG_WITH_AIF: opts.progWithAif ? '1' : '0',
    };
    if (opts.progWithAif) {
        // Match WinKFP's exact cabd-par names from FUN_00455780.
        if (opts.aifFgNr !== undefined)
            cabdPars['AIF_FG_NR'] = opts.aifFgNr;
        if (opts.aifDatum !== undefined)
            cabdPars['AIF_DATUM'] = opts.aifDatum;
        if (opts.aifAenderungsIndex !== undefined)
            cabdPars['AIF_AENDERUNGS_INDEX'] = opts.aifAenderungsIndex;
        if (opts.aifSwNr !== undefined)
            cabdPars['AIF_SW_NR'] = opts.aifSwNr;
        if (opts.aifBehoerdenNr !== undefined)
            cabdPars['AIF_BEHOERDEN_NR'] = opts.aifBehoerdenNr;
        if (opts.aifZbNr !== undefined)
            cabdPars['AIF_ZB_NR'] = opts.aifZbNr;
        if (opts.aifSerienNr !== undefined)
            cabdPars['AIF_SERIEN_NR'] = opts.aifSerienNr;
        if (opts.aifHaendlerNr !== undefined)
            cabdPars['AIF_HAENDLER_NR'] = opts.aifHaendlerNr;
        if (opts.aifKm !== undefined)
            cabdPars['AIF_KM'] = opts.aifKm;
        if (opts.aifProgNr !== undefined)
            cabdPars['AIF_PROG_NR'] = opts.aifProgNr;
        if (opts.aifAdresse !== undefined)
            cabdPars['AIF_ADRESSE'] = opts.aifAdresse;
    }
    const ediabasJobs = newEdiabasJobCounters();
    const countingProvider = wrapWithJobCounter(ediabas, ediabasJobs);
    const instrumented = firmwareSource
        ? buildInstrumentedFirmwareSource(firmwareSource, onProgress ? { onProgress } : {})
        : undefined;
    try {
        const handle = await startRuntime(ecu.ipoPath, {
            sgbd: ecu.sgbd,
            ediabas: countingProvider,
            cabdPars,
            workingDir: ecu.workingDir,
            firmwareSource: instrumented ?? firmwareSource,
            maxBinaryRetries: opts.maxBinaryRetries,
            retryBackoffMs: opts.retryBackoffMs,
        });
        await handle.runCabimain('SG_PROGRAMMIEREN');
        const status = handle.state.lastJobStatus;
        const report = {
            ok: status === 0,
            setjobstatus: status,
            cabdPars: Object.fromEntries(handle.state.cabdPars),
            ediabasJobs,
            slotTrace: [...handle.state.trace],
        };
        if (status !== 0)
            report.reason = `SG_PROGRAMMIEREN setjobstatus=${status}`;
        if (instrumented)
            report.firmwareStats = instrumented.stats;
        return report;
    }
    catch (err) {
        const report = {
            ok: false,
            setjobstatus: -1,
            reason: err instanceof Error ? err.message : String(err),
            cabdPars: {},
            ediabasJobs,
            slotTrace: [],
        };
        if (instrumented)
            report.firmwareStats = instrumented.stats;
        return report;
    }
}
/**
 * Dispatch `SG_AIF_SCHREIBEN` as a separate post-flash step.
 *
 * Only needed when the prior `SG_PROGRAMMIEREN` ran with
 * `PROG_WITH_AIF=0` — i.e. the AIF wasn't stamped inline. WinKFP's
 * FUN_00455780 sets the same AIF_* cabd-pars + dispatches
 * `SG_AIF_SCHREIBEN` in this path.
 */
export async function runAifSchreiben(ecu, ediabas, startRuntime, opts) {
    const cabdPars = {};
    if (opts.aifFgNr !== undefined)
        cabdPars['AIF_FG_NR'] = opts.aifFgNr;
    if (opts.aifDatum !== undefined)
        cabdPars['AIF_DATUM'] = opts.aifDatum;
    if (opts.aifAenderungsIndex !== undefined)
        cabdPars['AIF_AENDERUNGS_INDEX'] = opts.aifAenderungsIndex;
    if (opts.aifSwNr !== undefined)
        cabdPars['AIF_SW_NR'] = opts.aifSwNr;
    if (opts.aifBehoerdenNr !== undefined)
        cabdPars['AIF_BEHOERDEN_NR'] = opts.aifBehoerdenNr;
    if (opts.aifZbNr !== undefined)
        cabdPars['AIF_ZB_NR'] = opts.aifZbNr;
    if (opts.aifSerienNr !== undefined)
        cabdPars['AIF_SERIEN_NR'] = opts.aifSerienNr;
    if (opts.aifHaendlerNr !== undefined)
        cabdPars['AIF_HAENDLER_NR'] = opts.aifHaendlerNr;
    if (opts.aifKm !== undefined)
        cabdPars['AIF_KM'] = opts.aifKm;
    if (opts.aifProgNr !== undefined)
        cabdPars['AIF_PROG_NR'] = opts.aifProgNr;
    if (opts.aifAdresse !== undefined)
        cabdPars['AIF_ADRESSE'] = opts.aifAdresse;
    const ediabasJobs = newEdiabasJobCounters();
    const countingProvider = wrapWithJobCounter(ediabas, ediabasJobs);
    try {
        const handle = await startRuntime(ecu.ipoPath, {
            sgbd: ecu.sgbd,
            ediabas: countingProvider,
            cabdPars,
            workingDir: ecu.workingDir,
        });
        await handle.runCabimain('SG_AIF_SCHREIBEN');
        const status = handle.state.lastJobStatus;
        const report = {
            ok: status === 0,
            setjobstatus: status,
            cabdPars: Object.fromEntries(handle.state.cabdPars),
            ediabasJobs,
            slotTrace: [...handle.state.trace],
        };
        if (status !== 0)
            report.reason = `SG_AIF_SCHREIBEN setjobstatus=${status}`;
        return report;
    }
    catch (err) {
        return {
            ok: false,
            setjobstatus: -1,
            reason: err instanceof Error ? err.message : String(err),
            cabdPars: {},
            slotTrace: [],
            ediabasJobs,
        };
    }
}
//# sourceMappingURL=prog-sg.js.map